import React, { useState, useEffect } from "react";
import ClayLoadingIndicator from "@clayui/loading-indicator";
import Stepper from "../components/Stepper";
import { usePicklist } from "../contexts/PicklistProvider";
import userSubmit from "../utils/userSubmit";

import BaseInfo from "./steps/BaseInfo";
import SindicateInfo from "./steps/SindicateInfo";
import DependentInfo from "./steps/DependentInfo";
import PaymentInfo from "./steps/PaymentInfo";

import Modal from "../components/Modal";
import UserSubmitErrorMessage from "../errors/UserSubmitErrorMessage";

const steps = [
	{ key: "baseInfo", title: "Ficha Cadastral", component: BaseInfo },
	{
		key: "sindicateInfo",
		title: "Ficha Cadastral",
		component: SindicateInfo,
	},
	{
		key: "dependantInfo",
		title: "Ficha Cadastral",
		component: DependentInfo,
	},
	{ key: "paymentInfo", title: "Ficha Cadastral", component: PaymentInfo },
];

const RegisterUserForm = () => {
	const [submission, setSubmission] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [activeStep, setActiveStep] = useState(0);
	const [formData, setFormData] = useState({
		baseInfo: {},
		sindicateInfo: {},
		dependantInfo: {},
		paymentInfo: {},
	});

	const handleOpenModal = () => setIsModalOpen(true);
	const handleCloseModal = () => setIsModalOpen(false);

	const { getPicklistByCode, externalReferenceCodes, loading } =
		usePicklist();

	const picklistItemsMap = {
		estadoAtual: externalReferenceCodes.CURRENTSTATE,
		estadoCivil: externalReferenceCodes.CIVILSTATUS,
		lotacao: externalReferenceCodes.ORGAO,
		nivelDeEscolaridade: externalReferenceCodes.EDUCATIONLEVEL,
		sexo: externalReferenceCodes.GENDER,
		situation: externalReferenceCodes.SITUATION,
		tipoSanguineo: externalReferenceCodes.BLOODTYPE,
		uf: externalReferenceCodes.UF,
	};

	const picklistItems = loading
		? {}
		: Object.keys(picklistItemsMap).reduce((acc, key) => {
				acc[key] = getPicklistByCode(picklistItemsMap[key]);
				return acc;
		  }, {});

	function getPicklistItemByName(picklist, itemName) {
		const items = picklistItems[picklist] || [];
		return (
			items.find(
				(item) =>
					item.name === itemName ||
					item.name_i18n === itemName ||
					item.key === itemName
			) || null
		);
	}

	function getPicklistObject(category, value) {
		const picklistItem = getPicklistItemByName(category, value);
		return picklistItem
			? { key: picklistItem.key, name: picklistItem.name ?? value }
			: { key: value, name: value };
	}

	function parseUser(flattened) {
		const fields = [
			"sexo",
			"tipoSanguineo",
			"uf",
			"nivelDeEscolaridade",
			"estadoCivil",
			"estadoAtual",
			"lotacao",
		];

		fields.forEach((field) => {
			flattened[field] = getPicklistObject(field, flattened[field]);
		});

		// Criando sempre usuários filiados
		flattened.situation = getPicklistObject("situation", "affiliated");

		return flattened;
	}

	useEffect(() => {
		if (
			submission &&
			(!submission.status || submission.status.label !== "pending")
		) {
			handleOpenModal();
		}
	}, [submission]);

	const handleNextButtonClick = async () => {
		if (activeStep < steps.length - 1) {
			document
				.querySelector("#step-anchor")
				.scrollIntoView({ behavior: "smooth", block: "start" });
			setActiveStep((prevStep) => prevStep + 1);
		} else if (activeStep === steps.length - 1) {
			const flattened = {
				...formData.baseInfo,
				...formData.sindicateInfo,
				...formData.dependantInfo,
				...formData.paymentInfo,
			};

			const response = await userSubmit(parseUser(flattened));

			if (response?.error) {
				setSubmission(response);
				handleOpenModal();
			} else {
				setSubmission(response);
				handleSuccess();
			}
		}
	};

	const handleSubmitStep = (stepKey, data) => {
		setFormData((prevData) => {
			const updatedData = { ...prevData, [stepKey]: data };

			// Handle final submission if it's the last step
			if (activeStep === steps.length - 1) {
				const flattened = {
					...updatedData.baseInfo,
					...updatedData.sindicateInfo,
					...updatedData.dependantInfo,
					...updatedData.paymentInfo,
				};
				userSubmit(parseUser(flattened)).then((response) => {
					if (response?.error) {
						setSubmission(response);
						handleOpenModal();
					} else {
						setSubmission(response);
						handleSuccess();
					}
				});
			}
			return updatedData;
		});

		if (activeStep < steps.length - 1) {
			setActiveStep((prevStep) => prevStep + 1);
		}
	};

	const handleBackButtonClick = () => {
		if (activeStep > 0) {
			document
				.querySelector("#step-anchor")
				.scrollIntoView({ behavior: "smooth", block: "start" });
			setActiveStep((prevStep) => prevStep - 1);
		}
	};

	const handleUpdateStepData = (stepKey, data) => {
		setFormData((prevData) => ({
			...prevData,
			[stepKey]: data,
		}));
	};

	const handleSuccess = () => {
		setActiveStep(-1);
	};

	const StepComponent = steps[activeStep]?.component || null;

	return (
		<>
			<Modal
				show={isModalOpen}
				onClose={handleCloseModal}
				title="Ocorreu um erro"
			>
				<UserSubmitErrorMessage response={submission} locale="pt-BR" />
			</Modal>
			{activeStep === -1 ? (
				<div className="success-page py-5">
					<h2>Cadastro realizado com sucesso!</h2>
					<p>
						Obrigado por se registrar. Sua inscrição foi concluída
						com êxito. O Sinpol-DF irá analisar as informações e
						entrar em contato.
					</p>
					<button
						onClick={() => setActiveStep(0)}
						className="btn btn-outline-primary"
					>
						Voltar ao Início
					</button>
				</div>
			) : (
				<>
					<h3 className="py-5" id="step-anchor">
						Ficha Cadastral
					</h3>
					<Stepper
						activeStep={activeStep}
						steps={steps.map((step) => step.title)}
						title=""
					/>
					<div className="pb-5">
						{StepComponent ? (
							<StepComponent
								data={formData[steps[activeStep].key]}
								updateData={(data) =>
									handleUpdateStepData(
										steps[activeStep].key,
										data
									)
								}
								onNextStep={handleNextButtonClick}
								onBackStep={handleBackButtonClick}
								onSubmitStep={handleSubmitStep}
							/>
						) : (
							<ClayLoadingIndicator />
						)}
					</div>
				</>
			)}
		</>
	);
};

export default RegisterUserForm;
